import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, isApiError } from '@/shared/lib/apiClient';
import type { ApiSuccessResponse } from '@super-admin/shared-types';
import { normalizeAuthUser } from '../lib/normalizeAuthUser';
import { isMfaChallenge } from '../lib/loginResponse';
import {
  clearPendingMfaChallenge,
  getPendingMfaChallenge,
  setPendingMfaChallenge,
} from '../lib/pendingMfaChallenge';
import type { AuthContextValue, AuthUser, LoginResult } from '../types';

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

const ME_QUERY_KEY = ['auth', 'me'] as const;

function userFromLoginPayload(payload: unknown): AuthUser {
  const me = normalizeAuthUser(payload);
  if (!me) {
    throw new Error('Login succeeded but user profile was missing from the response');
  }
  return me;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [forcedNull, setForcedNull] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () =>
      apiClient
        .get<ApiSuccessResponse<AuthUser>>('/api/v1/auth/me')
        .then((r) => normalizeAuthUser(r)),
    retry: (count, err) => {
      if (isApiError(err) && err.status === 401) return false;
      return count < 1;
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const user = forcedNull ? null : isError ? null : (data ?? null);
  const isAuthLoading = isPending && !forcedNull;

  useEffect(() => {
    const handle = () => {
      setForcedNull(true);
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
    };
    window.addEventListener('auth:logout', handle);
    return () => window.removeEventListener('auth:logout', handle);
  }, [queryClient]);

  const applyAuthenticatedUser = useCallback(
    (me: AuthUser) => {
      setForcedNull(false);
      queryClient.setQueryData(ME_QUERY_KEY, me);
      return me;
    },
    [queryClient],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const res = await apiClient.post<ApiSuccessResponse<unknown>>('/api/v1/auth/login', {
        email,
        password,
      });

      const payload = res.data;
      if (isMfaChallenge(payload)) {
        setPendingMfaChallenge({
          email,
          password,
          mfaToken: payload.mfa_token,
        });
        return { kind: 'mfa_required' };
      }

      const me = userFromLoginPayload(payload);
      applyAuthenticatedUser(me);
      return { kind: 'authenticated', user: me };
    },
    [applyAuthenticatedUser],
  );

  const completeMfaLogin = useCallback(
    async (code: string): Promise<AuthUser> => {
      const challenge = getPendingMfaChallenge();
      if (!challenge) {
        throw new Error('MFA session expired. Please sign in again.');
      }

      const res = await apiClient.post<ApiSuccessResponse<unknown>>('/api/v1/auth/login', {
        email: challenge.email,
        password: challenge.password,
        mfa_token: challenge.mfaToken,
        mfa_code: code,
      });

      clearPendingMfaChallenge();
      const me = userFromLoginPayload(res.data);
      return applyAuthenticatedUser(me);
    },
    [applyAuthenticatedUser],
  );

  const logout = useCallback(async (): Promise<void> => {
    clearPendingMfaChallenge();
    await apiClient.post('/api/v1/auth/logout', {}).catch(() => {});
    setForcedNull(true);
    queryClient.clear();
  }, [queryClient]);

  const register = useCallback(
    async (data: { email: string; password: string; fullName: string; locale?: string }) => {
      const res = await apiClient.post<ApiSuccessResponse<{ id: string }>>(
        '/api/v1/auth/register',
        data,
      );
      return res.data;
    },
    [],
  );

  const refreshUser = useCallback(async (): Promise<void> => {
    setForcedNull(false);
    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [queryClient]);

  const completeProfile = useCallback(
    async (data: { fullName: string; companyName?: string }): Promise<AuthUser> => {
      const res = await apiClient.patch<ApiSuccessResponse<unknown>>(
        '/api/v1/auth/me/profile',
        data,
      );
      const me = normalizeAuthUser(res.data);
      if (!me) {
        throw new Error('Profile updated but user profile was missing from the response');
      }
      return applyAuthenticatedUser(me);
    },
    [applyAuthenticatedUser],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthLoading,
        login,
        completeMfaLogin,
        logout,
        register,
        refreshUser,
        completeProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
